import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  ExternalLink, 
  Filter,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Video, 
  Image, 
  Code, 
  Pencil, 
  Zap, 
  Headphones, 
  Users, 
  Palette, 
  Search, 
  Mic, 
  BarChart3, 
  Settings, 
  Building2, 
  FolderKanban,
  X,
  SlidersHorizontal,
  ArrowUpDown,
  ArrowDownAZ,
  ArrowUpAZ,
  Clock,
  Building,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { SEO } from '@/components/SEO';

const API_URL = import.meta.env.VITE_API_URL || '';

// Category icons mapping
const categoryIcons = {
  "Video Generation": Video,
  "Image Generation": Image,
  "Coding Assistants": Code,
  "Writing & Content": Pencil,
  "Productivity": Zap,
  "Customer Support": Headphones,
  "Meeting & Transcription": Users,
  "Design & Creative": Palette,
  "Research & Search": Search,
  "Voice & Audio": Mic,
  "Data & Analytics": BarChart3,
  "Developer Tools": Settings,
  "Enterprise & Automation": Building2,
  "Project Management": FolderKanban,
};

const ITEMS_PER_PAGE = 15;

// Sort options for the dropdown
const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A-Z)', icon: ArrowDownAZ },
  { value: 'name_desc', label: 'Name (Z-A)', icon: ArrowUpAZ },
  { value: 'newest', label: 'Newest First', icon: Clock },
  { value: 'oldest', label: 'Oldest First', icon: Clock },
  { value: 'provider_asc', label: 'Provider (A-Z)', icon: Building },
  { value: 'provider_desc', label: 'Provider (Z-A)', icon: Building },
];

// Check if platform was recently updated (within 30 days)
const isRecentlyUpdated = (updatedAt) => {
  if (!updatedAt) return false;
  const updateDate = new Date(updatedAt);
  const now = new Date();
  const daysDiff = Math.floor((now - updateDate) / (1000 * 60 * 60 * 24));
  return daysDiff <= 30;
};

export default function BrowseCategoryPage() {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [allCategories, setAllCategories] = useState([]);
  
  // Search, filter, and sort state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [pricingFilter, setPricingFilter] = useState(searchParams.get('pricing') || '');
  const [sortOption, setSortOption] = useState(searchParams.get('sort') || 'name_asc');

  const decodedCategory = decodeURIComponent(categoryName || '');
  const Icon = categoryIcons[decodedCategory] || Sparkles;

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setPage(1);
        // Update URL params
        const params = new URLSearchParams(searchParams);
        if (searchInput) {
          params.set('q', searchInput);
        } else {
          params.delete('q');
        }
        setSearchParams(params, { replace: true });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    // Fetch all categories for sidebar
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/platforms/categories/popular`);
        if (response.ok) {
          const data = await response.json();
          setAllCategories(data);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchPlatforms = async () => {
      setLoading(true);
      try {
        const skip = (page - 1) * ITEMS_PER_PAGE;
        let url = `${API_URL}/api/platforms/browse/category/${encodeURIComponent(decodedCategory)}?limit=${ITEMS_PER_PAGE}&skip=${skip}`;
        
        if (searchQuery) {
          url += `&search=${encodeURIComponent(searchQuery)}`;
        }
        if (pricingFilter) {
          url += `&pricing=${encodeURIComponent(pricingFilter)}`;
        }
        if (sortOption) {
          url += `&sort=${encodeURIComponent(sortOption)}`;
        }
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setPlatforms(data.platforms || []);
          setTotal(data.total || 0);
        }
      } catch (err) {
        console.error('Failed to fetch platforms:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (decodedCategory) {
      fetchPlatforms();
    }
  }, [decodedCategory, page, searchQuery, pricingFilter, sortOption]);

  // Reset search when category changes (but not on initial mount)
  const prevCategoryRef = useRef(categoryName);
  useEffect(() => {
    // Only reset if category actually changed (not on initial mount)
    if (prevCategoryRef.current !== categoryName && prevCategoryRef.current !== undefined) {
      setSearchInput('');
      setSearchQuery('');
      setPricingFilter('');
      setSortOption('name_asc');
      setPage(1);
      setSearchParams({}, { replace: true });
    }
    prevCategoryRef.current = categoryName;
  }, [categoryName]);

  const handlePricingFilter = (filter) => {
    const newFilter = pricingFilter === filter ? '' : filter;
    setPricingFilter(newFilter);
    setPage(1);
    const params = new URLSearchParams(searchParams);
    if (newFilter) {
      params.set('pricing', newFilter);
    } else {
      params.delete('pricing');
    }
    setSearchParams(params, { replace: true });
  };

  const handleSortChange = (newSort) => {
    setSortOption(newSort);
    setPage(1);
    const params = new URLSearchParams(searchParams);
    if (newSort && newSort !== 'name_asc') {
      params.set('sort', newSort);
    } else {
      params.delete('sort');
    }
    setSearchParams(params, { replace: true });
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setPricingFilter('');
    setSortOption('name_asc');
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters = searchQuery || pricingFilter || (sortOption && sortOption !== 'name_asc');

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const getPricingLabel = (platform) => {
    const pricing = platform.pricing;
    if (!pricing) return null;
    
    if (pricing.hasFreeTier || pricing.hasFreeeTier) {
      return { text: 'Free tier', variant: 'default' };
    }
    if (typeof pricing.paidPlans === 'string' && pricing.paidPlans) {
      return { text: pricing.paidPlans, variant: 'secondary' };
    }
    return null;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        pageType="custom"
        title={`${decodedCategory} AI Tools - GoCompareAI`}
        description={`Compare ${total}+ ${decodedCategory.toLowerCase()} AI tools and platforms. Find the best AI solution for your needs.`}
      />
      <Header />
      
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          {/* Back Button & Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="mb-4 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Home
            </Button>
            
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                  {decodedCategory}
                </h1>
                <p className="text-muted-foreground">
                  {total} AI tools and platforms
                </p>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar - Categories */}
            <aside className="lg:col-span-1">
              <Card className="sticky top-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    All Categories
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                    {allCategories.map((cat) => {
                      const CatIcon = categoryIcons[cat.name] || Sparkles;
                      const isActive = cat.name === decodedCategory;
                      return (
                        <button
                          key={cat.name}
                          onClick={() => {
                            setPage(1);
                            navigate(`/browse/${encodeURIComponent(cat.name)}`);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                            isActive 
                              ? 'bg-primary/10 text-primary font-medium' 
                              : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <CatIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="flex-1 truncate">{cat.name}</span>
                          <Badge variant="secondary" className="text-xs h-5 px-1.5">
                            {cat.count}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </aside>

            {/* Main Content - Platforms Grid */}
            <div className="lg:col-span-3">
              {/* Search and Filters Bar */}
              <div className="mb-6 space-y-3">
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={`Search within ${decodedCategory}...`}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchInput && (
                    <button
                      onClick={() => {
                        setSearchInput('');
                        setSearchQuery('');
                        setPage(1);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Filter Buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mr-2">
                    <SlidersHorizontal className="w-4 h-4" />
                    <span>Filter:</span>
                  </div>
                  <Button
                    variant={pricingFilter === 'free' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePricingFilter('free')}
                    className="h-7 text-xs"
                  >
                    Free Tier
                  </Button>
                  <Button
                    variant={pricingFilter === 'paid' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePricingFilter('paid')}
                    className="h-7 text-xs"
                  >
                    Paid Only
                  </Button>
                  
                  {/* Divider */}
                  <div className="w-px h-5 bg-border mx-1" />
                  
                  {/* Sort Dropdown */}
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mr-1">
                    <ArrowUpDown className="w-4 h-4" />
                    <span>Sort:</span>
                  </div>
                  <Select value={sortOption} onValueChange={handleSortChange}>
                    <SelectTrigger className="h-7 w-[140px] text-xs">
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SORT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="h-7 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Clear all
                    </Button>
                  )}
                </div>

                {/* Results count */}
                <div className="text-sm text-muted-foreground">
                  {loading ? (
                    <span>Searching...</span>
                  ) : (
                    <span>
                      {total} {total === 1 ? 'result' : 'results'}
                      {searchQuery && <span> for "<strong>{searchQuery}</strong>"</span>}
                      {pricingFilter && <span> with {pricingFilter === 'free' ? 'free tier' : 'paid only'}</span>}
                      {sortOption && sortOption !== 'name_asc' && (
                        <span> • sorted by {SORT_OPTIONS.find(o => o.value === sortOption)?.label.toLowerCase()}</span>
                      )}
                    </span>
                  )}
                </div>
              </div>

              {loading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <Card key={i}>
                      <CardContent className="p-4">
                        <Skeleton className="h-6 w-32 mb-2" />
                        <Skeleton className="h-4 w-24 mb-3" />
                        <Skeleton className="h-12 w-full mb-3" />
                        <Skeleton className="h-5 w-20" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : platforms.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Search className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    {hasActiveFilters ? (
                      <>
                        <p className="text-muted-foreground mb-2">
                          No platforms found matching your filters.
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">
                          Try adjusting your search or filters.
                        </p>
                        <Button
                          variant="outline"
                          onClick={clearFilters}
                        >
                          Clear Filters
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-muted-foreground">
                          No platforms found in this category.
                        </p>
                        <Button
                          variant="outline"
                          className="mt-4"
                          onClick={() => navigate('/')}
                        >
                          Back to Home
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {platforms.map((platform) => {
                      const pricingLabel = getPricingLabel(platform);
                      return (
                        <Card 
                          key={platform.id} 
                          className="hover:shadow-md transition-shadow cursor-pointer group"
                          onClick={() => navigate(`/platform/${platform.id}`)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                                  {platform.name}
                                </h3>
                                <p className="text-xs text-muted-foreground truncate">
                                  {platform.provider}
                                </p>
                              </div>
                              <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2" />
                            </div>
                            
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-3 min-h-[2.5rem]">
                              {platform.summary || platform.description?.substring(0, 100)}
                            </p>

                            <div className="flex items-center gap-2 flex-wrap">
                              {isRecentlyUpdated(platform.updatedAt) && (
                                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Updated
                                </Badge>
                              )}
                              {pricingLabel && (
                                <Badge variant={pricingLabel.variant} className="text-xs">
                                  {pricingLabel.text}
                                </Badge>
                              )}
                              {platform.tags?.slice(0, 2).map(tag => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-8">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      
                      <div className="flex items-center gap-1">
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (page <= 3) {
                            pageNum = i + 1;
                          } else if (page >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = page - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={page === pageNum ? "default" : "ghost"}
                              size="sm"
                              onClick={() => setPage(pageNum)}
                              className="w-8 h-8 p-0"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
